import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Button,
  CircularProgress, Snackbar, Alert, Avatar, Chip, Divider, Tooltip,
  LinearProgress, Paper, Grid, Select, MenuItem, InputLabel,
  FormControl, InputAdornment, IconButton, Stack
} from '@mui/material';
import {
  Save as SaveIcon,
  RefreshOutlined as RefreshIcon,
  InfoOutlined as InfoIcon,
  Search as SearchIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  CheckCircleOutline as CheckIcon,
  WarningAmberOutlined as WarnIcon,
  ErrorOutline as ErrorIcon,
  PersonOutlined as PersonIcon,
  TuneOutlined as TuneIcon,
  AccessTimeOutlined as ClockIcon,
  EditOutlined as EditIcon,
} from '@mui/icons-material';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const getDefaultFromDate = () => {
  const d = new Date();
  const day = d.getDay(); // 0=Sun,1=Mon,...6=Sat
  const diffToMonday = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diffToMonday);
  return d.toISOString().split('T')[0];
};
const getDefaultToDate = () => {
  const d = new Date();
  const day = d.getDay(); // 0=Sun,1=Mon,...6=Sat
  const diffToSaturday = day === 0 ? 6 : 6 - day;
  d.setDate(d.getDate() + diffToSaturday);
  return d.toISOString().split('T')[0];
};

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────
const C = {
  primary: '#0B1F3D',
  navy:    '#1a3a6b',
  accent:  '#1976D2',
  green:   '#2E7D32',
  orange:  '#E65100',
  red:     '#C62828',
  yellow:  '#F57F17',
  border:  '#E2E8F0',
  surface: '#F8FAFC',
  muted:   '#64748B',
};

const ROW_OPTIONS = [5, 10, 15, 25];

const apiUrl   = process.env.REACT_APP_BASE_URL;
const getToken = () => sessionStorage.getItem('token');
const authH    = () => ({ Authorization: getToken() });
const postH    = () => ({ 'Content-Type': 'application/json', Authorization: getToken() });

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Returns status label + colors based on assigned vs ideal/max */
const getStatus = (assigned, idealLoad, maxPeriods) => {
  if (assigned === 0)          return { label: 'Unassigned',   color: '#78909C', bg: '#ECEFF1' };
  if (assigned > maxPeriods)   return { label: 'Overloaded',   color: C.red,     bg: '#FFEBEE' };
  if (assigned >= idealLoad)   return { label: 'Healthy',      color: C.green,   bg: '#E8F5E9' };
  if (assigned >= idealLoad * 0.6)
                               return { label: 'Moderate',     color: C.accent,  bg: '#E3F2FD' };
  return                              { label: 'Under Target',  color: C.yellow,  bg: '#FFF8E1' };
};

/** Tiny progress bar shared in the table */
const LoadBar = ({ assigned, maxPeriods, idealLoad }) => {
  const pct      = maxPeriods > 0 ? Math.min((assigned / maxPeriods) * 100, 100) : 0;
  const barColor = assigned > maxPeriods ? C.red
                 : assigned >= idealLoad ? C.green
                 : C.yellow;
  return (
    <Tooltip title={`${assigned} / ${maxPeriods} periods (${pct.toFixed(1)}%)`} arrow>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            flex: 1, height: 8, borderRadius: 8, bgcolor: '#EEF2F7',
            '& .MuiLinearProgress-bar': { borderRadius: 8, bgcolor: barColor },
          }}
        />
        <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 36, color: C.primary, textAlign: 'right' }}>
          {pct.toFixed(0)}%
        </Typography>
      </Box>
    </Tooltip>
  );
};

/** Styled number input */
const NumInput = ({ value, onChange, min = 0, max = 200 }) => (
  <TextField
    type="number"
    size="small"
    value={value}
    onChange={e => onChange(Number(e.target.value))}
    inputProps={{ min, max, style: { textAlign: 'center', fontWeight: 700, padding: '6px 4px', width: 64 } }}
    sx={{
      '& .MuiOutlinedInput-root': {
        borderRadius: 2,
        '&:hover fieldset': { borderColor: C.accent },
        '&.Mui-focused fieldset': { borderColor: C.accent },
      },
    }}
  />
);

/** Table header cell */
const TH = ({ children, align = 'left', width, tooltip }) => (
  <TableCell align={align} sx={{ bgcolor: '#F1F5F9', fontWeight: 700, fontSize: '0.76rem', color: '#475569', py: 1.4, letterSpacing: 0.2, ...(width ? { width } : {}) }}>
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
      {children}
      {tooltip && (
        <Tooltip title={tooltip} arrow>
          <InfoIcon sx={{ fontSize: 13, color: '#94A3B8', cursor: 'help' }} />
        </Tooltip>
      )}
    </Box>
  </TableCell>
);

/** Pagination bar */
const Pager = ({ total, page, rowsPerPage, onPage, onRows }) => {
  const pages = Math.max(Math.ceil(total / rowsPerPage), 1);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.2, borderTop: `1px solid ${C.border}`, flexWrap: 'wrap', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" color="text.secondary">Rows per page:</Typography>
        <Select
          value={rowsPerPage}
          onChange={e => { onRows(Number(e.target.value)); onPage(1); }}
          size="small"
          sx={{ fontSize: '0.78rem', height: 28, minWidth: 62 }}
        >
          {ROW_OPTIONS.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
        </Select>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {Math.min((page - 1) * rowsPerPage + 1, total)}–{Math.min(page * rowsPerPage, total)} of {total}
        </Typography>
        <IconButton size="small" onClick={() => onPage(p => p - 1)} disabled={page <= 1}><PrevIcon fontSize="small" /></IconButton>
        <IconButton size="small" onClick={() => onPage(p => p + 1)} disabled={page >= pages}><NextIcon fontSize="small" /></IconButton>
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
export default function TeacherLoadConfig() {
  // Raw data from APIs
  const [summaryList, setSummaryList] = useState([]); // /summary response
  const [configList,  setConfigList]  = useState([]); // /config response

  // Editable map: teacherId -> { id, maxPeriods, idealLoad }
  const [configMap, setConfigMap] = useState({});

  // Track which rows have been modified (to highlight them)
  const [dirtyIds, setDirtyIds] = useState(new Set());

  // UI state
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [search,   setSearch]   = useState('');
  const [page,     setPage]     = useState(1);
  const [rowsPP,   setRowsPP]   = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ── Branch state (mirrors Employee.js pattern) ──────────────────────────
  const [isMaster,         setIsMaster]         = useState(false);
  const [branches,         setBranches]         = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate,   setToDate]   = useState(getDefaultToDate());

  // ── Resolve active branchId for use in all API calls ──────────────────
  // Master users: from dropdown selection
  // Non-master users: from sessionStorage (set at login)
  const activeBranchId = isMaster
    ? selectedBranchId
    : sessionStorage.getItem('branchId');

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  // ── Fetch branch list (master users only) ─────────────────────────────
  const fetchBranches = async () => {
    try {
      const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
        method: 'POST',
        headers: postH(),
        body: JSON.stringify({}),
      });
      if (response.ok) {
        const data = await response.json();
        setBranches(data);
        // Auto-fill first branch for master user when branches load
        if (data.length > 0) {
          setSelectedBranchId(data[0].branchId);
        }
      } else {
        console.error('Failed to fetch branches');
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  // ── On mount: detect master user, load branches or read from session ──
  useEffect(() => {
    const isMasterUser = sessionStorage.getItem('is_Master') === 'true';
    setIsMaster(isMasterUser);

    if (isMasterUser) {
      // fetchBranches sets selectedBranchId → triggers the data load via selectedBranchId effect
      fetchBranches();
    } else {
      // Non-master: branchId already in session, activeBranchId resolves it directly
    }
  }, []);

  // ── Fetch: runs on mount (non-master) and whenever branch changes ─────
  // Guard: don't fetch until we have a valid branchId
useEffect(() => {
    if (!activeBranchId) return;
    fetchData();
  }, [selectedBranchId, fromDate, toDate]);// selectedBranchId change covers master; non-master fires once on mount via activeBranchId

  // ── Fetch data ─────────────────────────────────────────────────────────
  // APIs integrated on this page:
  //   1. GET /adv/teacher-load/summary?Branch_id={branchId}   — teacher list with load stats
  //   2. GET /adv/teacher-load/config?Branch_id={branchId}    — saved max/ideal config per teacher
  //   3. POST /adv/teacher-load/config/save?Branch_id={branchId} — save config (Branch_id as query param, payload in body)
  //   4. POST /Branch/GetBranch                               — branch list (master users only, no branch filter needed)
  // Branch_id is passed as a query parameter to all adv/ GET and POST endpoints,
  // consistent with the Swagger spec (integer $int32, query).
  const fetchData = async () => {
    setLoading(true);
    setDirtyIds(new Set());
    try {
    const summaryParams = new URLSearchParams({ Branch_id: activeBranchId });
      if (fromDate) summaryParams.set('fromDate', fromDate);
      if (toDate)   summaryParams.set('toDate',   toDate);

      const [sRes, cRes] = await Promise.all([
        // GET /adv/teacher-load/summary — Branch_id (+ optional date range) as query params
        fetch(`${apiUrl}/adv/teacher-load/summary?${summaryParams}`, { headers: authH() }),
        // GET /adv/teacher-load/config — Branch_id as query param (not date-based)
        fetch(`${apiUrl}/adv/teacher-load/config?Branch_id=${activeBranchId}`,  { headers: authH() }),
      ]);

      // /summary: [{ teacherId, teacherName, totalAssignedPeriods, totalAvailablePeriods,
      //              freePeriods, utilizationPercent, loadStatus, isClassTeacher, mobile }]
      const teachers = await sRes.json().then(d => Array.isArray(d) ? d : []);

      // /config: [{ id, teacherId, teacherName, maxPeriods, idealLoad, updatedDate }]
      const configs  = await cRes.json().then(d => Array.isArray(d) ? d : []);

      setSummaryList(teachers);
      setConfigList(configs);

      // Build editable map seeded from config; fall back to defaults for unconfigured teachers
      const cfgById = {};
      configs.forEach(c => { cfgById[c.teacherId] = { id: c.id, maxPeriods: c.maxPeriods, idealLoad: c.idealLoad, updatedDate: c.updatedDate }; });

      const map = {};
      teachers.forEach(t => {
        map[t.teacherId] = cfgById[t.teacherId]
          ? { ...cfgById[t.teacherId] }
          : { id: 0, maxPeriods: 45, idealLoad: 38, updatedDate: null };
      });
      setConfigMap(map);
    } catch (err) {
      console.error(err);
      showSnackbar('Failed to load data. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ── Handle field change ────────────────────────────────────────────────────
  const handleChange = (teacherId, field, value) => {
    setConfigMap(prev => ({
      ...prev,
      [teacherId]: { ...prev[teacherId], [field]: Math.max(0, Number(value)) },
    }));
    setDirtyIds(prev => new Set(prev).add(teacherId));
  };

  // ── Save all ───────────────────────────────────────────────────────────────
  // save response: { msg: "...", status: "Succeed!" }  (lowercase keys)
  // POST /adv/teacher-load/config/save — Branch_id as query param, payload array in body
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = summaryList.map(t => ({
        id:         configMap[t.teacherId]?.id        || 0,
        teacherId:  t.teacherId,
        maxPeriods: configMap[t.teacherId]?.maxPeriods ?? 45,
        idealLoad:  configMap[t.teacherId]?.idealLoad  ?? 38,
      }));

      // Branch_id passed as query param to the save endpoint (same pattern as GET endpoints)
      const res  = await fetch(`${apiUrl}/adv/teacher-load/config/save?Branch_id=${activeBranchId}`, {
        method: 'POST', headers: postH(), body: JSON.stringify(payload),
      });
      const data = await res.json();

      // response uses lowercase: { msg, status }
      if (data?.status === 'Succeed!' || res.ok) {
        showSnackbar(data?.msg || 'Configuration saved successfully!', 'success');
        fetchData(); // refresh to get updated IDs + dates
      } else {
        showSnackbar(data?.msg || 'Failed to save. Please try again.', 'error');
      }
    } catch (err) {
      console.error(err);
      showSnackbar('An error occurred. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Derived / filtered data ───────────────────────────────────────────────
  const filtered = useMemo(() =>
    summaryList.filter(t =>
      !search ||
      t.teacherName?.toLowerCase().includes(search.toLowerCase()) ||
      t.mobile?.includes(search)
    ), [summaryList, search]);

  const paged = useMemo(() =>
    filtered.slice((page - 1) * rowsPP, page * rowsPP),
    [filtered, page, rowsPP]);

  // KPI stats
  const kpis = useMemo(() => {
    const total     = summaryList.length;
    const assigned  = summaryList.filter(t => (t.totalAssignedPeriods || 0) > 0).length;
    const healthy   = summaryList.filter(t => {
      const cfg = configMap[t.teacherId];
      return cfg && (t.totalAssignedPeriods || 0) >= cfg.idealLoad;
    }).length;
    const overloaded = summaryList.filter(t => {
      const cfg = configMap[t.teacherId];
      return cfg && (t.totalAssignedPeriods || 0) > cfg.maxPeriods;
    }).length;
    const configured = Object.values(configMap).filter(c => c.id > 0).length;
    return { total, assigned, healthy, overloaded, configured, dirty: dirtyIds.size };
  }, [summaryList, configMap, dirtyIds]);

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <Box sx={{ bgcolor: C.surface, minHeight: '100vh', pb: 5 }}>

      {/* ── Page Header ── */}
      <Box sx={{
        background: `linear-gradient(135deg, ${C.primary} 0%, ${C.navy} 100%)`,
        py: 3.5, px: { xs: 2, md: 4 }, mb: 3,
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800, letterSpacing: -0.5 }}>
              Teacher Load Configuration
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.3 }}>
              Set max periods &amp; ideal load targets for each teacher
            </Typography>
          </Box>

          {/* Right: Branch Dropdown (master users only) + Refresh button */}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>

            {/* ── Branch Dropdown — visible only for Master/Admin users ── */}
            {isMaster && (
              <FormControl size="small" sx={{ width: 220 }}>
                <InputLabel
                  sx={{
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    letterSpacing: '0.6px',
                    textTransform: 'uppercase',
                    top: '-2px',
                    '&.Mui-focused': { color: '#64B5F6' },
                    '&.MuiFormLabel-filled': { color: 'rgba(255,255,255,0.7)' },
                  }}
                >
                  Branch
                </InputLabel>
                <Select
                  value={selectedBranchId || ''}
                  onChange={(e) => {
                    setSelectedBranchId(e.target.value);
                    // Reset search and pagination when branch changes
                    setSearch('');
                    setPage(1);
                  }}
                  label="Branch"
                  startAdornment={
                    <InputAdornment position="start">
                      <LocationOnIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.5)', ml: 0.5 }} />
                    </InputAdornment>
                  }
                  sx={{
                    color: '#ffffff',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    letterSpacing: '0.3px',
                    backgroundColor: 'rgba(255,255,255,0.07)',
                    backdropFilter: 'blur(8px)',
                    borderRadius: '8px',
                    transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.15)',
                      borderWidth: '1px',
                      transition: 'border-color 0.2s ease',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.11)',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255,255,255,0.35)',
                      },
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      boxShadow: '0 0 0 3px rgba(100,181,246,0.18)',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#64B5F6',
                        borderWidth: '1.5px',
                      },
                    },
                    '& .MuiSelect-icon': {
                      color: 'rgba(255,255,255,0.5)',
                      transition: 'color 0.2s ease',
                    },
                    '&.Mui-focused .MuiSelect-icon': { color: '#64B5F6' },
                    '& .MuiSelect-select': { py: 1.1 },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        mt: 0.5,
                        borderRadius: '10px',
                        border: '1px solid rgba(11,31,61,0.12)',
                        boxShadow: '0 8px 32px rgba(11,31,61,0.18), 0 2px 8px rgba(0,0,0,0.08)',
                        overflow: 'hidden',
                        '& .MuiList-root': { py: 0.5 },
                      },
                    },
                  }}
                >
                  {branches.map((branch) => (
                    <MenuItem
                      key={branch.branchId}
                      value={branch.branchId}
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#0B1F3D',
                        px: 2, py: 1.1, mx: 0.5,
                        borderRadius: '6px',
                        transition: 'background-color 0.15s ease',
                        '&:hover': { backgroundColor: 'rgba(11,31,61,0.07)' },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(25,118,210,0.1)',
                          color: '#1565C0',
                          fontWeight: 600,
                          '&:hover': { backgroundColor: 'rgba(25,118,210,0.15)' },
                        },
                      }}
                    >
                      {branch.branchName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

          {/* <TextField
              label="From" type="date" size="small"
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                width: 150,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px', bgcolor: 'rgba(255,255,255,0.07)', color: '#fff',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
                },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
                '& input': { color: '#fff' },
              }}
            />
            <TextField
              label="To" type="date" size="small"
              value={toDate}
              onChange={e => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                width: 150,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px', bgcolor: 'rgba(255,255,255,0.07)', color: '#fff',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
                },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
                '& input': { color: '#fff' },
              }}
            /> */}

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchData}
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)', fontWeight: 700, '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}
            >
              Refresh
            </Button>

          </Box>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ px: { xs: 1.5, md: 3 } }}>

        {/* ── KPI Cards ── */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { icon: <PersonIcon sx={{ fontSize: 22 }} />,  label: 'Total Teachers', value: kpis.total,      sub: `${kpis.assigned} active this week`,   color: C.accent  },
            { icon: <TuneIcon   sx={{ fontSize: 22 }} />,  label: 'Configured',     value: kpis.configured,  sub: 'Have saved config',                   color: '#7B1FA2' },
            { icon: <CheckIcon  sx={{ fontSize: 22 }} />,  label: 'Healthy Load',   value: kpis.healthy,     sub: 'At or above ideal load',               color: C.green   },
            { icon: <WarnIcon   sx={{ fontSize: 22 }} />,  label: 'Overloaded',     value: kpis.overloaded,  sub: 'Exceeds max periods',                  color: C.red     },
          ].map((k, i) => (
            <Grid item xs={6} md={3} key={i}>
              <Paper sx={{
                p: 2, borderRadius: 2.5, border: `1px solid ${C.border}`, boxShadow: 'none',
                display: 'flex', alignItems: 'center', gap: 2,
                transition: 'box-shadow 0.18s',
                '&:hover': { boxShadow: '0 4px 16px rgba(11,31,61,0.10)' },
              }}>
                <Box sx={{ width: 46, height: 46, borderRadius: 2, bgcolor: k.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', color: k.color, flexShrink: 0 }}>
                  {k.icon}
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.67rem' }}>
                    {k.label}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: k.color, lineHeight: 1.1, mt: 0.2 }}>
                    {k.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{k.sub}</Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* ── Info Banner ── */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: '#EFF6FF', border: '1px solid #BFDBFE', display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <InfoIcon sx={{ color: '#1D4ED8', mt: 0.2, fontSize: 20, flexShrink: 0 }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1D4ED8', mb: 0.3 }}>How configuration works</Typography>
            <Typography variant="body2" sx={{ color: '#1E40AF', lineHeight: 1.6 }}>
              <strong>Max Periods</strong> — the hard ceiling for a teacher's weekly assigned periods (going over marks them <em>Overloaded</em>).&nbsp;
              <strong>Ideal Load</strong> — the optimal weekly target (reaching this marks them <em>Healthy</em>).&nbsp;
              Changes are highlighted in orange until saved. Click <strong>Save All</strong> to persist all changes at once.
            </Typography>
          </Box>
        </Paper>

        {/* ── Main Card ── */}
        <Card sx={{ borderRadius: 3, boxShadow: '0 2px 16px rgba(11,31,61,0.09)', border: `1px solid ${C.border}` }}>

          {/* Card toolbar */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: C.primary }}>
                Per-Teacher Load Settings
              </Typography>
              
              <Typography variant="caption" color="text.secondary">
                {kpis.total} teachers · {kpis.dirty > 0 ? (
                  <Box component="span" sx={{ color: C.orange, fontWeight: 700 }}>
                    {kpis.dirty} unsaved change{kpis.dirty !== 1 ? 's' : ''}
                  </Box>
                ) : 'All changes saved'}
              </Typography>
            </Box>
            <Box>
             <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving || loading}
              sx={{
                bgcolor: '#fff', color: C.primary, fontWeight: 800,
                '&:hover': { bgcolor: '#E8EDF4' },
                '&:disabled': { bgcolor: 'rgba(255,255,255,0.4)', color: 'rgba(255,255,255,0.7)' }, mr:2,
              }}
            >
              {saving ? 'Saving…' : `Save All${kpis.dirty > 0 ? ` (${kpis.dirty} changed)` : ''}`}
            </Button>
            {/* Search */}
            <TextField
              size="small"
              placeholder="Search by name or mobile…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 17, color: '#94A3B8' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 280, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            /></Box>
          </Box>

          <Divider />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10, flexDirection: 'column', gap: 2 }}>
              <CircularProgress sx={{ color: C.primary }} />
              <Typography variant="body2" color="text.secondary">Loading teacher data…</Typography>
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 520 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TH width={44}>#</TH>
                      <TH width={220}>Teacher</TH>
                      <TH align="center" width={110}>Assigned Periods</TH>
                      <TH align="center" width={100}>Total</TH>
                      <TH align="center" width={100}>Utilization</TH>
                      <TH align="center" width={130}
                        tooltip="Hard ceiling — going over marks teacher as Overloaded">
                        Max Periods
                      </TH>
                      <TH align="center" width={120}
                        tooltip="Optimal weekly target — reaching this marks teacher as Healthy">
                        Ideal Load
                      </TH>
                      <TH align="center" width={120}>Status</TH>
                      <TH width={170}>Load Meter</TH>
                      <TH align="center" width={110}>Last Updated</TH>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {paged.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ py: 6, color: C.muted }}>
                          No teachers found matching your search.
                        </TableCell>
                      </TableRow>
                    ) : paged.map((teacher, idx) => {
                      const rowNum   = (page - 1) * rowsPP + idx + 1;
                      const cfg      = configMap[teacher.teacherId] || { id: 0, maxPeriods: 45, idealLoad: 38 };
                      const assigned = teacher.totalAssignedPeriods || 0;
                      const avail    = teacher.totalAvailablePeriods || 0;
                      const utilPct  = avail > 0 ? ((assigned / avail) * 100) : 0;
                      const status   = getStatus(assigned, cfg.idealLoad, cfg.maxPeriods);
                      const isDirty  = dirtyIds.has(teacher.teacherId);
                      const lastUpd  = cfg.updatedDate
                        ? new Date(cfg.updatedDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })
                        : '—';

                      return (
                        <TableRow
                          key={teacher.teacherId}
                          hover
                          sx={{
                            '&:last-child td': { border: 0 },
                            bgcolor: isDirty ? '#FFFBEB' : 'inherit',
                            borderLeft: isDirty ? `3px solid ${C.orange}` : '3px solid transparent',
                            transition: 'background 0.2s',
                          }}
                        >
                          {/* # */}
                          <TableCell sx={{ color: C.muted, fontSize: '0.72rem', pl: isDirty ? 1.5 : 2 }}>
                            {isDirty ? <EditIcon sx={{ fontSize: 12, color: C.orange }} /> : rowNum}
                          </TableCell>

                          {/* Teacher name */}
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                              <Avatar sx={{ width: 34, height: 34, bgcolor: C.primary, fontSize: '0.82rem', fontWeight: 700 }}>
                                {teacher.teacherName?.charAt(0)?.toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: C.primary, lineHeight: 1.3 }}>
                                  {teacher.teacherName}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
                                  {teacher.isClassTeacher === 'Yes' && (
                                    <Chip label="Class Teacher" size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#E3F2FD', color: '#1565C0', fontWeight: 700 }} />
                                  )}
                                  {teacher.mobile && (
                                    <Typography variant="caption" sx={{ color: C.muted, fontSize: '0.68rem' }}>
                                      {teacher.mobile}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          </TableCell>

                          {/* Assigned */}
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 800, color: C.primary, fontSize: '1rem' }}>
                              {assigned}
                            </Typography>
                          </TableCell>

                          {/* Available */}
                          <TableCell align="center">
                            <Typography variant="body2" color="text.secondary">{avail}</Typography>
                          </TableCell>

                          {/* Utilization % */}
                          <TableCell align="center">
                            <Box sx={{
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              px: 1.2, py: 0.3, borderRadius: 1.5,
                              bgcolor: utilPct >= 70 ? '#E8F5E9' : utilPct >= 30 ? '#E3F2FD' : '#FFF8E1',
                            }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: utilPct >= 70 ? C.green : utilPct >= 30 ? C.accent : C.yellow }}>
                                {utilPct.toFixed(1)}%
                              </Typography>
                            </Box>
                          </TableCell>

                          {/* Max Periods input */}
                          <TableCell align="center">
                            <NumInput
                              value={cfg.maxPeriods}
                              onChange={val => handleChange(teacher.teacherId, 'maxPeriods', val)}
                            />
                          </TableCell>

                          {/* Ideal Load input */}
                          <TableCell align="center">
                            <NumInput
                              value={cfg.idealLoad}
                              onChange={val => handleChange(teacher.teacherId, 'idealLoad', val)}
                            />
                          </TableCell>

                          {/* Status chip */}
                          <TableCell align="center">
                            <Chip
                              label={status.label}
                              size="small"
                              sx={{ bgcolor: status.bg, color: status.color, fontWeight: 700, fontSize: '0.7rem', borderRadius: 1.5, height: 22 }}
                            />
                          </TableCell>

                          {/* Load meter */}
                          <TableCell>
                            <LoadBar assigned={assigned} maxPeriods={cfg.maxPeriods} idealLoad={cfg.idealLoad} />
                            {/* Ideal marker line */}
                            <Box sx={{ mt: 0.3, display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" sx={{ fontSize: '0.62rem', color: C.muted }}>0</Typography>
                              <Typography variant="caption" sx={{ fontSize: '0.62rem', color: C.accent }}>
                                Ideal: {cfg.idealLoad}
                              </Typography>
                              <Typography variant="caption" sx={{ fontSize: '0.62rem', color: C.muted }}>
                                Max: {cfg.maxPeriods}
                              </Typography>
                            </Box>
                          </TableCell>

                          {/* Last updated */}
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.4 }}>
                              <ClockIcon sx={{ fontSize: 12, color: C.muted }} />
                              <Typography variant="caption" sx={{ color: C.muted, fontSize: '0.7rem' }}>
                                {lastUpd}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <Pager
                total={filtered.length}
                page={page}
                rowsPerPage={rowsPP}
                onPage={setPage}
                onRows={setRowsPP}
              />
            </>
          )}
        </Card>

        {/* ── Bottom save bar (sticky reminder when dirty) ── */}
        {kpis.dirty > 0 && (
          <Box sx={{
            position: 'sticky', bottom: 16, mt: 2,
            bgcolor: C.primary, borderRadius: 2.5,
            px: 3, py: 1.5,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            boxShadow: '0 8px 32px rgba(11,31,61,0.28)',
            flexWrap: 'wrap', gap: 1.5,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: C.orange, animation: 'pulse 1.5s infinite' }} />
              <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
                You have <strong>{kpis.dirty} unsaved change{kpis.dirty !== 1 ? 's' : ''}</strong>. Don't forget to save!
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={fetchData}
                sx={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.3)', fontWeight: 700, '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } }}
              >
                Discard
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{ bgcolor: '#fff', color: C.primary, fontWeight: 800, '&:hover': { bgcolor: '#E8EDF4' } }}
              >
                {saving ? 'Saving…' : 'Save Now'}
              </Button>
            </Box>
          </Box>
        )}

      </Container>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4500}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}